/**
 * VerwaltungPage – zentrales Verwaltungs-Zentrum (Zahnrad in der Navbar).
 *
 * Sub-Bereiche:
 *  - Benutzer & Zugriffe: ALLE Benutzer (interne Mitarbeiter + externe
 *    Tool-Portal-User) in einer Tabelle; pro Benutzer alle Modul-/Tool-
 *    Freigaben, Stammdaten, Passwort, aktiv.
 *  - Arbeitsnachweis: bewährte Admin-Maske eingebettet (Anträge, Statistik,
 *    Export, Import, Konfiguration).
 *  - Tool-Portal: externe Benutzer-Verwaltung (tools.sijelly.com).
 *
 * Nutzt AUSSCHLIESSLICH bestehende Admin-APIs beider Pools + den neuen
 * Modul-Katalog-Endpoint — keine neue Backend-User-API.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../api/config";
import { useAuth } from "../context/AuthContext";
import ArbeitsnachweisPa from "./ArbeitsnachweisPa";
import AdminToolsPage from "./AdminToolsPage";
import "./VerwaltungPage.css";

const api = (path, opts = {}) =>
  fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      "X-MA-Token": localStorage.getItem("maToken") || "",
      ...(opts.headers || {}),
    },
  });

const NEU_INTERN = { kuerzel: "", vorname: "", nachname: "", passwort: "", rolle: "mitarbeiter", zugang_typ: "vollzugang" };
const NEU_EXTERN = { login: "", name: "", firma: "", passwort: "" };

export default function VerwaltungPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("benutzer");

  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState("");
  const [interne, setInterne] = useState([]);
  const [externe, setExterne] = useState([]);
  const [modulKatalog, setModulKatalog] = useState([]);
  const [kachelGruppen, setKachelGruppen] = useState([]);   // Welle 101: Freigabe je Kachel
  const [toolKatalog, setToolKatalog] = useState([]);

  const [offen, setOffen] = useState(null);          // "i-<id>" | "e-<id>"
  const [zugriffe, setZugriffe] = useState({});       // {key: bool} des geöffneten Users
  const [zugriffeDirty, setZugriffeDirty] = useState(false);
  const [pwWert, setPwWert] = useState("");
  const [saving, setSaving] = useState(false);
  const [neu, setNeu] = useState(null);               // {typ:"intern"|"extern", form}
  const [suchtext, setSuchtext] = useState("");

  const lade = useCallback(async () => {
    setLaden(true); setFehler("");
    try {
      const [mi, tb, mk, tk] = await Promise.all([
        api("/arbeitsnachweis/admin/mitarbeiter"),
        api("/tools-portal/admin/benutzer"),
        api("/benutzer/module-katalog"),
        api("/tools-portal/admin/katalog"),
      ]);
      if (!mi.ok || !tb.ok) throw new Error("Laden fehlgeschlagen (Admin-Rechte?)");
      const miData = await mi.json();
      setInterne(miData.mitarbeiter || miData || []);
      setExterne((await tb.json()).benutzer || []);
      if (mk.ok) {
        const k = await mk.json();
        setModulKatalog(k.katalog || []);
        setKachelGruppen(k.gruppen || []);
      }
      if (tk.ok) setToolKatalog((await tk.json()).katalog || []);
    } catch (e) {
      setFehler(String(e.message || e));
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => { lade(); }, [lade]);

  // ── Zugriffe eines Users laden/öffnen ────────────────────────────────────
  const oeffne = async (typ, u) => {
    const key = `${typ}-${u.id}`;
    if (offen === key) { setOffen(null); return; }
    setOffen(key); setZugriffe({}); setZugriffeDirty(false); setPwWert("");
    try {
      if (typ === "i") {
        const r = await api(`/arbeitsnachweis/admin/mitarbeiter/${u.id}/module`);
        const data = await r.json();
        setZugriffe(data.module || {});
      } else {
        setZugriffe(Object.fromEntries(
          toolKatalog.map((t) => [t.tool_key, (u.tools || []).includes(t.tool_key)])));
      }
    } catch (_) {}
  };

  const toggleZugriff = (k) => {
    setZugriffe((z) => ({ ...z, [k]: !z[k] }));
    setZugriffeDirty(true);
  };
  const kachelKeys = kachelGruppen.flatMap((g) => g.kacheln.map((k) => k.key));
  const gruppeSetzen = (gruppe, an) => {
    setZugriffe((z) => ({ ...z, ...Object.fromEntries(gruppe.kacheln.map((k) => [k.key, an])) }));
    setZugriffeDirty(true);
  };
  const alleZugriffe = (an) => {
    const keys = offen?.startsWith("i-")
      ? (kachelKeys.length ? kachelKeys : modulKatalog.map((m) => m.key))
      : toolKatalog.map((t) => t.tool_key);
    setZugriffe(Object.fromEntries(keys.map((k) => [k, an])));
    setZugriffeDirty(true);
  };

  const speichereZugriffe = async (typ, u) => {
    setSaving(true);
    try {
      const r = typ === "i"
        ? await api(`/arbeitsnachweis/admin/mitarbeiter/${u.id}/module`,
                    { method: "PUT", body: JSON.stringify(zugriffe) })
        : await api(`/tools-portal/admin/benutzer/${u.id}/tools`,
                    { method: "PUT", body: JSON.stringify(zugriffe) });
      if (!r.ok) throw new Error("Speichern fehlgeschlagen");
      setZugriffeDirty(false);
      lade();
    } catch (e) { alert(String(e.message || e)); }
    finally { setSaving(false); }
  };

  const speicherePasswort = async (typ, u) => {
    if (!pwWert) { alert("Bitte neues Passwort eingeben"); return; }
    setSaving(true);
    try {
      const r = typ === "i"
        ? await api(`/arbeitsnachweis/admin/mitarbeiter/${u.id}`,
                    { method: "PUT", body: JSON.stringify({ passwort: pwWert }) })
        : await api(`/tools-portal/admin/benutzer/${u.id}/passwort`,
                    { method: "PUT", body: JSON.stringify({ passwort: pwWert }) });
      if (!r.ok) throw new Error("Passwort setzen fehlgeschlagen");
      setPwWert(""); alert("Passwort gesetzt.");
    } catch (e) { alert(String(e.message || e)); }
    finally { setSaving(false); }
  };

  const toggleAktiv = async (typ, u) => {
    const r = typ === "i"
      ? await api(`/arbeitsnachweis/admin/mitarbeiter/${u.id}`,
                  { method: "PUT", body: JSON.stringify({ aktiv: u.aktiv ? 0 : 1 }) })
      : await api(`/tools-portal/admin/benutzer/${u.id}`,
                  { method: "PUT", body: JSON.stringify({ aktiv: !u.aktiv }) });
    if (!r.ok) alert("Ändern fehlgeschlagen");
    lade();
  };

  const anlegen = async () => {
    const f = neu.form;
    setSaving(true);
    try {
      if (neu.typ === "intern") {
        if (!f.kuerzel.trim() || !f.passwort) throw new Error("Kürzel und Passwort sind Pflicht");
        const r = await api("/arbeitsnachweis/admin/mitarbeiter",
                            { method: "POST", body: JSON.stringify(f) });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.detail || "Anlegen fehlgeschlagen");
      } else {
        if (!f.login.trim() || !f.passwort) throw new Error("Login und Passwort sind Pflicht");
        const r = await api("/tools-portal/admin/benutzer",
                            { method: "POST", body: JSON.stringify({ ...f, tools: {} }) });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.detail || "Anlegen fehlgeschlagen");
      }
      setNeu(null); lade();
    } catch (e) { alert(String(e.message || e)); }
    finally { setSaving(false); }
  };

  if (user && user.rolle !== "admin") {
    navigate("/");
    return null;
  }

  const filter = suchtext.trim().toLowerCase();
  const passt = (s) => !filter || (s || "").toLowerCase().includes(filter);
  const interneGefiltert = interne.filter((m) =>
    passt(m.kuerzel) || passt(m.vorname) || passt(m.nachname));
  const externeGefiltert = externe.filter((b) =>
    passt(b.login) || passt(b.name) || passt(b.firma));

  const zugriffsListe = offen?.startsWith("i-")
    ? modulKatalog.map((m) => ({ key: m.key, label: m.label }))
    : toolKatalog.map((t) => ({ key: t.tool_key, label: `${t.icon || ""} ${t.name}`.trim() }));

  const renderZeile = (typ, u) => {
    const key = `${typ}-${u.id}`;
    const istOffen = offen === key;
    const name = typ === "i"
      ? `${u.vorname || ""} ${u.nachname || ""}`.trim() || "—"
      : u.name || "—";
    const kennung = typ === "i" ? u.kuerzel : u.login;
    const info = typ === "i"
      ? (u.rolle === "admin" ? "Admin" : (u.zugang_typ === "nur_zeiterfassung" ? "nur Zeiterfassung" : "Mitarbeiter"))
      : (u.firma || "extern");
    return (
      <React.Fragment key={key}>
        <tr className={u.aktiv ? "" : "inaktiv"}>
          <td><span className={`vw-typ ${typ === "i" ? "intern" : "extern"}`}>{typ === "i" ? "Intern" : "Extern"}</span></td>
          <td><code>{kennung}</code></td>
          <td>{name}</td>
          <td>{info}</td>
          <td><span className={`vw-status ${u.aktiv ? "an" : "aus"}`}>{u.aktiv ? "aktiv" : "deaktiviert"}</span></td>
          <td className="vw-aktionen">
            <button className="vw-btn small" onClick={() => oeffne(typ, u)}>
              {istOffen ? "▲ Zuklappen" : "▼ Zugriffe & mehr"}
            </button>
            <button className="vw-btn small" onClick={() => toggleAktiv(typ, u)}>
              {u.aktiv ? "Deaktivieren" : "Aktivieren"}
            </button>
          </td>
        </tr>
        {istOffen && (
          <tr className="vw-detail-row">
            <td colSpan={6}>
              <div className="vw-detail">
                <div className="vw-detail-block">
                  <div className="vw-detail-titel">
                    Zugriffe ({typ === "i" ? "Module" : "Tools"})
                    <span className="vw-detail-hint">
                      {typ === "i" && " — keine Häkchen gesetzt = User sieht alles (Alt-Verhalten)"}
                    </span>
                  </div>
                  {typ === "i" && kachelGruppen.length > 0 ? (
                    // Welle 101: jede Kachel einzeln, gruppiert wie die Startseite
                    <div className="vw-gruppen">
                      {kachelGruppen.map((g) => {
                        const an = g.kacheln.filter((k) => zugriffe[k.key]).length;
                        return (
                          <div key={g.titel} className="vw-gruppe">
                            <label className="vw-gruppe-kopf">
                              <input type="checkbox" checked={an === g.kacheln.length}
                                ref={(el) => { if (el) el.indeterminate = an > 0 && an < g.kacheln.length; }}
                                onChange={() => gruppeSetzen(g, an < g.kacheln.length)} />
                              <span>{g.titel}</span>
                              <small>{an}/{g.kacheln.length}</small>
                            </label>
                            {g.kacheln.map((k) => (
                              <label key={k.key} className="vw-check vw-kachel">
                                <input type="checkbox" checked={!!zugriffe[k.key]} onChange={() => toggleZugriff(k.key)} />
                                <span>{k.label}</span>
                              </label>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="vw-checks">
                      {zugriffsListe.map(({ key: k, label }) => (
                        <label key={k} className="vw-check">
                          <input type="checkbox" checked={!!zugriffe[k]} onChange={() => toggleZugriff(k)} />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  <div className="vw-detail-buttons">
                    <button className="vw-btn small" onClick={() => alleZugriffe(true)}>Alle an</button>
                    <button className="vw-btn small" onClick={() => alleZugriffe(false)}>Alle aus</button>
                    <button className="vw-btn small primary" disabled={!zugriffeDirty || saving}
                      onClick={() => speichereZugriffe(typ, u)}>
                      {saving ? "Speichert…" : "Zugriffe speichern"}
                    </button>
                  </div>
                </div>
                <div className="vw-detail-block">
                  <div className="vw-detail-titel">Passwort zurücksetzen</div>
                  <div className="vw-pw-row">
                    <input className="vw-input" type="text" placeholder="Neues Passwort…"
                      value={pwWert} onChange={(e) => setPwWert(e.target.value)} />
                    <button className="vw-btn small" disabled={saving} onClick={() => speicherePasswort(typ, u)}>Setzen</button>
                  </div>
                  {typ === "i" && (
                    <div className="vw-detail-hint" style={{ marginTop: 10 }}>
                      Arbeitszeit-Stammdaten (Soll, Urlaub, Bundesland …) →{" "}
                      <button className="vw-link" onClick={() => setTab("arbeitsnachweis")}>Bereich Arbeitsnachweis</button>
                    </div>
                  )}
                </div>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="vw-page">
      <div className="vw-header">
        <button className="vw-back" onClick={() => navigate("/")}>← Home</button>
        <h1>⚙️ Verwaltung</h1>
        <div className="vw-tabs">
          <button className={`vw-tab ${tab === "benutzer" ? "active" : ""}`} onClick={() => setTab("benutzer")}>👥 Benutzer & Zugriffe</button>
          <button className={`vw-tab ${tab === "arbeitsnachweis" ? "active" : ""}`} onClick={() => setTab("arbeitsnachweis")}>⏱ Arbeitsnachweis</button>
          <button className={`vw-tab ${tab === "toolportal" ? "active" : ""}`} onClick={() => setTab("toolportal")}>🧰 Tool-Portal</button>
        </div>
      </div>

      {tab === "benutzer" && (
        <div className="vw-body">
          <div className="vw-toolbar">
            <input className="vw-input vw-suche" placeholder="Suchen (Kürzel, Name, Firma)…"
              value={suchtext} onChange={(e) => setSuchtext(e.target.value)} />
            <button className="vw-btn primary" onClick={() => setNeu({ typ: "intern", form: { ...NEU_INTERN } })}>+ Neuer Benutzer</button>
          </div>
          {fehler && <div className="vw-fehler">{fehler}</div>}
          {laden ? <div className="vw-laden">Lädt…</div> : (
            <div className="vw-tabelle-wrap">
              <table className="vw-tabelle">
                <thead>
                  <tr><th>Typ</th><th>Kennung</th><th>Name</th><th>Rolle / Firma</th><th>Status</th><th>Aktionen</th></tr>
                </thead>
                <tbody>
                  {interneGefiltert.map((m) => renderZeile("i", m))}
                  {externeGefiltert.map((b) => renderZeile("e", b))}
                  {!interneGefiltert.length && !externeGefiltert.length && (
                    <tr><td colSpan={6} className="vw-leer">Keine Benutzer gefunden.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "arbeitsnachweis" && (
        <div className="vw-embed"><ArbeitsnachweisPa adminOnly={true} /></div>
      )}

      {tab === "toolportal" && (
        <div className="vw-embed"><AdminToolsPage /></div>
      )}

      {neu && (
        <div className="vw-modal-bg">
          <div className="vw-modal">
            <h2>Neuer Benutzer</h2>
            <div className="vw-typ-wahl">
              <label className="vw-check">
                <input type="radio" checked={neu.typ === "intern"}
                  onChange={() => setNeu({ typ: "intern", form: { ...NEU_INTERN } })} />
                <span>Intern (Mitarbeiter)</span>
              </label>
              <label className="vw-check">
                <input type="radio" checked={neu.typ === "extern"}
                  onChange={() => setNeu({ typ: "extern", form: { ...NEU_EXTERN } })} />
                <span>Extern (Tool-Portal)</span>
              </label>
            </div>
            {neu.typ === "intern" ? (
              <>
                <label className="vw-label">Kürzel *<input className="vw-input" value={neu.form.kuerzel}
                  onChange={(e) => setNeu((n) => ({ ...n, form: { ...n.form, kuerzel: e.target.value } }))} /></label>
                <label className="vw-label">Vorname<input className="vw-input" value={neu.form.vorname}
                  onChange={(e) => setNeu((n) => ({ ...n, form: { ...n.form, vorname: e.target.value } }))} /></label>
                <label className="vw-label">Nachname<input className="vw-input" value={neu.form.nachname}
                  onChange={(e) => setNeu((n) => ({ ...n, form: { ...n.form, nachname: e.target.value } }))} /></label>
                <label className="vw-label">Passwort *<input className="vw-input" type="text" value={neu.form.passwort}
                  onChange={(e) => setNeu((n) => ({ ...n, form: { ...n.form, passwort: e.target.value } }))} /></label>
                <label className="vw-label">Rolle
                  <select className="vw-input" value={neu.form.rolle}
                    onChange={(e) => setNeu((n) => ({ ...n, form: { ...n.form, rolle: e.target.value } }))}>
                    <option value="mitarbeiter">Mitarbeiter</option>
                    <option value="admin">Admin</option>
                  </select></label>
                <label className="vw-label">Zugang
                  <select className="vw-input" value={neu.form.zugang_typ}
                    onChange={(e) => setNeu((n) => ({ ...n, form: { ...n.form, zugang_typ: e.target.value } }))}>
                    <option value="vollzugang">Vollzugang</option>
                    <option value="nur_zeiterfassung">Nur Zeiterfassung</option>
                  </select></label>
              </>
            ) : (
              <>
                <label className="vw-label">Login *<input className="vw-input" value={neu.form.login}
                  onChange={(e) => setNeu((n) => ({ ...n, form: { ...n.form, login: e.target.value } }))} /></label>
                <label className="vw-label">Firma<input className="vw-input" value={neu.form.firma}
                  onChange={(e) => setNeu((n) => ({ ...n, form: { ...n.form, firma: e.target.value } }))} /></label>
                <label className="vw-label">Name<input className="vw-input" value={neu.form.name}
                  onChange={(e) => setNeu((n) => ({ ...n, form: { ...n.form, name: e.target.value } }))} /></label>
                <label className="vw-label">Passwort *<input className="vw-input" type="text" value={neu.form.passwort}
                  onChange={(e) => setNeu((n) => ({ ...n, form: { ...n.form, passwort: e.target.value } }))} /></label>
                <div className="vw-detail-hint">Tools danach über „Zugriffe & mehr" freischalten.</div>
              </>
            )}
            <div className="vw-modal-buttons">
              <button className="vw-btn" onClick={() => setNeu(null)} disabled={saving}>Abbrechen</button>
              <button className="vw-btn primary" onClick={anlegen} disabled={saving}>
                {saving ? "Legt an…" : "Anlegen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
